import { Tooltip2 } from '@blueprintjs/popover2';
import { getShiftSlotTheme } from '../../components/roster-utilities';
import { IRosterShift } from '../../types';
import { ROSTER_DAILY_CONFIG } from '../roster-daily-config';
import { getTimelineCardWidth, getTimelineOffset } from './card-width-utils';
import { BookingTypeIcon } from 'common-components/tags';
import ShiftSlotStatusTag from 'common-components/tags/ShiftSlotStatusTag';
import { Text } from 'common-components/typography';
import moment from 'moment';
import React from 'react';
import { BookingType, ServiceType } from 'utilities/enum-utils';
import { Paper, UnstyledButton, Avatar, Group } from '@good/ui/core';
import { useBookings } from 'views/bookings-v2/books-view-context/bookings-view-context';
import { ROSTER_TAB } from '../../components/roster-common-enums';

const { TIMELINE_CARD_HEIGHT, TIMELINE_ROW_HEIGHT } = ROSTER_DAILY_CONFIG;

interface WorkerShiftCardProps {
  shift: IRosterShift;
  rowIndex: number;
}

/* This component takes a start/end time, and calculates the position/width within a timeline. */
export function WorkerShiftCard({ shift, rowIndex }: WorkerShiftCardProps) {
  const { shiftStartDateTime, shiftEndDateTime, shiftSlotStatus, serviceName } = shift;
  const mStartDate = moment(shiftStartDateTime);
  const mEndDate = moment(shiftEndDateTime);
  const startTimeDisplay = mStartDate.format('h:mm a');
  const endTimeDisplay = mEndDate.format('h:mm a');
  const cardWidth = getTimelineCardWidth(shiftStartDateTime, shiftEndDateTime);
  const leftOffset = getTimelineOffset(shiftStartDateTime);
  const topOffset = rowIndex === 0 ? 4 : rowIndex * TIMELINE_ROW_HEIGHT + 4;
  const { bgColor, borderColor } = getShiftSlotTheme(shiftSlotStatus);
  const onSelectRosterItem = useBookings((selector) => selector.onSelectRosterItem);
  const selectedRosterItem = useBookings((selector) => selector.selectedRosterItem);
  const isSelected = selectedRosterItem?.bookingId === shift.bookingId;

  const name = `${shift.customerFirstName ?? ''} ${shift.customerLastName ?? ''}`;

  return (
    <>
      <UnstyledButton
        onClick={() => onSelectRosterItem({ item: shift, type: 'workerShift', tab: ROSTER_TAB.DAILY })}
        display='flex'
        w={cardWidth}
        top={topOffset}
        left={leftOffset}
        h={TIMELINE_CARD_HEIGHT}
        pos='absolute'
        style={{
          zIndex: 10,
          boxShadow: isSelected ? '0 0 0 2px var(--mantine-color-lavender-6)' : '',
          borderRadius: 'calc(var(--mantine-radius-md) - 2px)',
        }}
      >
        <Paper
          p={6}
          w={cardWidth}
          withBorder
          h={TIMELINE_CARD_HEIGHT}
          shadow={isSelected ? 'xl' : 'xs'}
          bg={bgColor}
          style={{
            borderColor: borderColor,
            overflow: 'hidden',
          }}
          className='anim-fade-in'
        >
          <div className={'flex-row justify-start align-center '}>
            <ShiftSlotStatusTag status={shiftSlotStatus} size={'small'} />
            {shift.serviceType === ServiceType.INDIVIDUAL && shift.bookingId && (
              <Group gap={2} align='center' ml='xs'>
                <Tooltip2 content={<Text color={'white'}>{name}</Text>} position={'bottom-right'}>
                  <Avatar size={20} name={name} color='initials' />
                </Tooltip2>
              </Group>
            )}
          </div>
          <div className='pt-x2-small flex-column line-height-100'>
            <div className='text-overflow-ellipsis overflow-hidden whitespace-nowrap'>
              <Text lineHeight={100} size='small' className='whitespace-nowrap'>
                <b>{startTimeDisplay}</b> - {endTimeDisplay}
              </Text>
            </div>
            <div className={'flex-row align-center'}>
              <BookingTypeIcon
                type={
                  shift.bookingType === BookingType.ACTIVITY_RECORD ? BookingType.ACTIVITY_RECORD : shift.serviceType
                }
                className={'mr-x-small'}
              />
              <div className='text-overflow-ellipsis overflow-hidden whitespace-nowrap'>
                <Text lineHeight={100} size='regular' className='whitespace-nowrap'>
                  {serviceName}
                </Text>
              </div>
            </div>
          </div>
        </Paper>
      </UnstyledButton>
    </>
  );
}
